import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { RowActions, useModal, useToast } from '../../../../../../components'
import { FaturaView } from '../../core/_models'
import {
  IconPaths,
  Permission,
  showAlertLoading,
  showConfirmAlert,
  showError,
  SituacaoFatura,
} from '../../../../../../../_metronic/helpers'
import { FaturaModal } from '../../components/modal/FaturaModal'
import { deleteFatura } from '../../core/_requests'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useT } from '_metronic/i18n/Metronici18n'
import { useAuth } from 'app/modules/auth/core/Auth'

type IProps = {
  fatura: FaturaView
}

export const FaturaActionsCell: FC<IProps> = ({ fatura }) => {
  const { t } = useT()
  const { refetch } = useQueryResponse()
  const { filters } = useListFilters()
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const nome = fatura.descricao || `${t('fatura.label')} ${fatura.id}`

  // Instituição do filtro (não-super-admin vê os atendimentos dentro dela).
  const idEmpresa = filters.empresa?.value
  const isSuperAdmin =
    currentUser?.grupo?.nome === Permission.SuperAdmin ||
    currentUser?.grupo?.nome === Permission.SuperGerente

  const gerada = fatura.situacao === SituacaoFatura.Gerada
  const autorizada = fatura.situacao === SituacaoFatura.Autorizada

  const openModal = (action: 'autorizar' | 'cancelar' | 'pagar' | 'adicional') =>
    showModal({
      title: t('modal.fatura', { action }),
      content: <FaturaModal action={action} idFatura={fatura.id} />,
    })

  // Excluir é destrutivo: confirmação com a consequência.
  const askDelete = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Excluir fatura?',
      text: `${nome} será excluída. Essa ação não pode ser desfeita.`,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (!confirmed) return

    try {
      showAlertLoading()
      await deleteFatura(fatura.id)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi excluída.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  const handleAtendimentos = () =>
    navigate(`atendimentos/${fatura.id}${idEmpresa && !isSuperAdmin ? `/${idEmpresa}` : ''}`)

  return (
    <RowActions
      itemName={nome}
      primaryAction={{
        label: t('dropdown_item.visualizar_atendimentos'),
        onClick: handleAtendimentos,
        icon: IconPaths.Search,
      }}
      items={[
        {
          label: t('dropdown_item.atualizar_valor_adicional'),
          onClick: () => openModal('adicional'),
          hidden: !gerada,
        },
        {
          label: t('dropdown_item.autorizar'),
          onClick: () => openModal('autorizar'),
          hidden: !gerada,
        },
        { label: t('dropdown_item.pagar'), onClick: () => openModal('pagar'), hidden: !autorizada },
        { label: t('common.cancel'), onClick: () => openModal('cancelar'), hidden: !gerada },
        {
          label: t('common.delete'),
          onClick: askDelete,
          danger: true,
          divider: true,
          hidden: !gerada,
        },
      ]}
    />
  )
}
